import React from 'react';
import { ArrowLeft, AlertCircle, Car, ChevronRight, LogOut, RefreshCcw, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface ProfilePageProps {
  onBack: () => void;
  onNavigateToEmergency: () => void;
  onNavigateToMyVehicle: () => void;
  onChangeSession: () => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({
  onBack,
  onNavigateToEmergency,
  onNavigateToMyVehicle,
  onChangeSession,
}) => {
  const { user, profile, signOut } = useAuth();

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Invitado';
  const email = user?.email || 'Sin cuenta';

  const handleCloseSession = async () => {
    await signOut();
    onBack();
  };

  const handleChangeSession = async () => {
    await signOut();
    onChangeSession();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-6"
        >
          <ArrowLeft size={18} />
          Volver
        </button>

        <h1 className="text-4xl font-black text-gray-900 mb-2">Perfil</h1>
        <p className="text-gray-500 mb-8">Gestiona tu cuenta y accesos rápidos</p>

        <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="text-blue-600" size={36} />
            </div>
            <div className="flex-1">
              <p className="text-3xl font-black text-gray-900">{displayName}</p>
              <p className="text-gray-500 mt-1">{email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
            <button
              onClick={handleChangeSession}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition-colors"
            >
              <RefreshCcw size={16} />
              Cambiar sesión
            </button>
            <button
              onClick={handleCloseSession}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-100 transition-colors"
            >
              <LogOut size={16} />
              Cerrar sesión
            </button>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Accesos rápidos</h3>
          <div className="space-y-2">
            <button
              onClick={onNavigateToMyVehicle}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Car className="text-blue-600" size={18} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Mi vehículo</p>
                  <p className="text-sm text-gray-500">Tipo y combustible</p>
                </div>
              </div>
              <ChevronRight className="text-gray-400" size={18} />
            </button>
            <button
              onClick={onNavigateToEmergency}
              className="w-full rounded-xl border border-red-200 bg-red-50 p-4 flex items-center justify-between text-left hover:bg-red-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={18} />
                </div>
                <div>
                  <p className="font-semibold text-red-700">Emergencias</p>
                  <p className="text-sm text-red-600">Contactos de ayuda rápida</p>
                </div>
              </div>
              <ChevronRight className="text-red-400" size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
